import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Printer, Save } from 'lucide-react';
import { TestPicker, SelectedItems } from './TestPicker';
import { printLabBill, rupee } from './labPrint';
import { errMsg } from './labApi';

const EMPTY = { patient_name: '', age: '', sex: '', phone: '', referral_doctor: '' };

export const ModeToggle = ({ value, onChange, testId }) => (
  <div className="inline-flex rounded-lg border overflow-hidden" data-testid={testId}>
    {['cash', 'upi'].map((m) => (
      <button type="button" key={m} onClick={() => onChange(m)} data-testid={`${testId}-${m}`}
        className={`px-3 py-1.5 text-xs font-bold uppercase ${value === m ? 'bg-slate-900 text-white' : 'bg-white text-slate-600'}`}>{m}</button>
    ))}
  </div>
);

export const LabBillForm = ({ api, tests, referrals, onSaved }) => {
  const [form, setForm] = useState(EMPTY);
  const [items, setItems] = useState([]);
  const [discount, setDiscount] = useState('');
  const [advance, setAdvance] = useState('');
  const [mode, setMode] = useState('cash');
  const [saving, setSaving] = useState(false);

  const gross = items.reduce((s, i) => s + (parseFloat(i.rate) || 0), 0);
  const total = Math.max(gross - (parseFloat(discount) || 0), 0);
  const balance = Math.max(total - (parseFloat(advance) || 0), 0);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (print) => {
    if (!form.patient_name.trim()) return toast.error('Enter patient name');
    if (!items.length) return toast.error('Add at least one test');
    setSaving(true);
    try {
      const bill = await api.post('/bills', {
        ...form,
        items: items.map((i) => ({ name: i.name, rate: parseFloat(i.rate) || 0 })),
        discount: parseFloat(discount) || 0,
        advance: parseFloat(advance) || 0,
        advance_mode: mode,
      });
      toast.success(`Bill ${bill.bill_no} saved`);
      if (print) printLabBill(bill);
      setForm(EMPTY); setItems([]); setDiscount(''); setAdvance(''); setMode('cash');
      onSaved();
    } catch (e) {
      toast.error(errMsg(e, 'Could not save bill'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="p-5 space-y-4" data-testid="lab-bill-form">
      <div className="grid grid-cols-6 gap-2">
        <div className="col-span-6 space-y-1"><Label className="text-xs">Patient Name *</Label>
          <Input value={form.patient_name} onChange={set('patient_name')} className="h-10 text-base font-semibold" data-testid="lab-patient-name" /></div>
        <div className="col-span-2 space-y-1"><Label className="text-xs">Age</Label>
          <Input value={form.age} onChange={set('age')} placeholder="35" className="h-9" data-testid="lab-patient-age" /></div>
        <div className="col-span-2 space-y-1"><Label className="text-xs">Sex</Label>
          <select value={form.sex} onChange={set('sex')} className="h-9 w-full rounded-md border border-input bg-white px-2 text-sm" data-testid="lab-patient-sex">
            <option value="">Select</option><option>Male</option><option>Female</option><option>Other</option>
          </select></div>
        <div className="col-span-2 space-y-1"><Label className="text-xs">Phone</Label>
          <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D/g, '').slice(0, 10) })} inputMode="numeric" className="h-9" data-testid="lab-patient-phone" /></div>
        <div className="col-span-6 space-y-1"><Label className="text-xs">Referral Doctor</Label>
          <Input list="lab-referrals" value={form.referral_doctor} onChange={set('referral_doctor')} placeholder="Dr. ..." className="h-9" data-testid="lab-referral-doctor" />
          <datalist id="lab-referrals">{referrals.map((r) => <option key={r} value={r} />)}</datalist></div>
      </div>

      <TestPicker tests={tests} items={items} setItems={setItems} />
      <SelectedItems items={items} setItems={setItems} />

      <div className="rounded-xl bg-slate-50 border p-3 space-y-2" data-testid="lab-bill-totals">
        <div className="flex justify-between text-sm"><span>Rate total</span><span className="font-semibold" data-testid="lab-gross">{rupee(gross)}</span></div>
        <div className="flex justify-between items-center text-sm"><span>Discount</span>
          <Input inputMode="decimal" value={discount} onChange={(e) => setDiscount(e.target.value.replace(/[^0-9.]/g, ''))} placeholder="0" className="h-8 w-28 text-right" data-testid="lab-discount" /></div>
        <div className="flex justify-between text-lg font-black border-t pt-2"><span>Final Amount</span><span data-testid="lab-final-amount">{rupee(total)}</span></div>
        <div className="flex justify-between items-center gap-2 text-sm flex-wrap"><span>Advance</span>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <ModeToggle value={mode} onChange={setMode} testId="lab-advance-mode" />
            <Button type="button" size="sm" variant="outline" className="h-8 text-xs" onClick={() => setAdvance(String(total))} data-testid="lab-advance-full">Full</Button>
            <Input inputMode="decimal" value={advance} onChange={(e) => setAdvance(e.target.value.replace(/[^0-9.]/g, ''))} placeholder="0" className="h-8 w-28 text-right" data-testid="lab-advance" />
          </div></div>
        {(parseFloat(advance) || 0) > total && <p className="text-xs font-semibold text-red-600" data-testid="lab-advance-warning">Advance is more than the final amount</p>}
        <div className={`flex justify-between font-bold ${balance > 0 ? 'text-red-600' : 'text-emerald-700'}`}><span>Balance</span><span data-testid="lab-balance">{rupee(balance)}</span></div>
      </div>

      <div className="flex gap-2">
        <Button type="button" disabled={saving} onClick={() => save(true)} className="flex-1 bg-teal-700 hover:bg-teal-800 text-white h-10" data-testid="lab-save-print">
          <Printer className="h-4 w-4 mr-2" /> Save & Print
        </Button>
        <Button type="button" disabled={saving} variant="outline" onClick={() => save(false)} className="h-10" data-testid="lab-save">
          <Save className="h-4 w-4 mr-2" /> Save
        </Button>
      </div>
    </Card>
  );
};
